import { Dayjs } from "dayjs";
import { Amounts } from "./Amounts";
import { CouponSource, CouponType } from "./Coupon";
import { Guest } from "./Guest";
import { Payment } from "./Payment";
import { Unit } from "./Unit";

export interface LineItem {
    system?: boolean;
    id: ID;
    type: LineItemType;
    quantity: number | null;
    name: string;
    code: LineItemCode;
    object: LineItemObject;
    visibility: LineItemVisibility;
    label: string;
    amount: number;
    /** Only serialized for Xola admins; `traveler` includes amounts the seller never sees. */
    amounts?: Amounts;
    collected?: number; // Service fee collected
    price: number | null;
    couponCode?: { id: ID; code: string };
    appliedCode?: string;
    coupon?: { id: ID; name?: string; type?: CouponType; source?: CouponSource };
    voidedAt?: Dayjs;
    createdAt: Dayjs;
    updatedAt?: Dayjs;
    createdBy: { id: ID };
    template: { id: ID };
    traveler?: { id: ID; name?: string };
    status: string;
    payment?: Payment;
    _priceStrategy?: string;
    includeInAmount: boolean;
    destination?: LineItemDestination;
    guests: Guest[];
    fundingSource?: { id: ID };
    fundingDestination?: { id: ID };
    purchaseItem?: { id: ID };
    purchase?: { id: ID };
    source: LineItemSource;
    multiplier?: number;
    refundToStoreCredit?: boolean;
    sequence?: number;
    templateCode?: LineItemCode;
    voidingLineItem?: string;
    redeemedCode?: { id: ID; code: string };
    redeemedFor?: { id: ID; object: string };
    action?: RedemptionAction;
    seller?: { id: ID };
    appliedOn?: { id: ID }[];
}

export enum LineItemCode {
    DEMOGRAPHIC = "demographic_subtotal",
    PRIMARY_UNIT = "primary_unit_subtotal",
    EQUIPMENT = "equipment_subtotal",
    ADDON = "merchandise_subtotal",
    PICKUP = "pickup_subtotal",
    TAX = "tax_subtotal",
    FEE = "fee_subtotal",
    UNIT = "unit_subtotal",
    DISCOUNT = "discount_subtotal",
    PRE_TAX_FEE = "pre_tax_fee_subtotal",
    PRE_TAX = "pre_tax_subtotal",
    PRE_PARTNER_FEE_ITEM = "pre_partner_fee_item_subtotal",
    PRE_PARTNER_FEE = "pre_partner_fee_subtotal",
    GIFT_OFFER_DISCOUNT = "gift_offer_discount",
    PACKAGE_DISCOUNT = "package_discount",
    PRIVATE_UPGRADE = "private_upgrade",
    AFFILIATE_DISCOUNT = "affiliate_discount",
    AFFILIATE_DEPOSIT = "affiliate_deposit",
    AFFILIATE_COMMISSION = "affiliate_commission",
    PER_OUTING_PRICE = "per_outing_price",
    GIFT = "gift",
    PROCESSING_FEES = "processing_fees",
    PARTNER_FEES = "partner_fees",
    COUPON_DISCOUNT = "coupon_discount",
    MEMBERSHIP_DISCOUNT = "membership_discount",
    PARTNER_PACKAGE_DISCOUNT = "partner_package_discount",
}

export enum LineItemType {
    DEMOGRAPHIC = "demographic",
    DEMOGRAPHIC_SECONDARY = "demographic_secondary",
    EQUIPMENT = "equipment",
    MEMBERSHIP = "membership",
    WARE = "ware",
    ADDON = "merchandise",
    ADJUSTMENT = "adjustment",
    TAXED_ADJUSTMENT = "taxed_adjustment",
    PAYMENT = "payment",
    REFUND = "refund",
    REFUND_COMMISSION = "refund_commission",
    AFFILIATE_DEPOSIT = "affiliate_deposit",
    DISCOUNT = "discount",
    COUPON_DISCOUNT = "coupon_discount",
    AFFILIATE_DISCOUNT = "affiliate_discount",
    AFFILIATE_COMMISSION = "affiliate_commission",
    AFFILIATE_DISCOUNT_OVERRIDE = "affiliate_discount_override",
    AFFILIATE_COMMISSION_OVERRIDE = "affiliate_commission_override",
    PARTNER_FEE = "partner_fee",
    CANCELLATION_PROTECTION_FEE = "cancellation_protection_fee",
    TAX = "tax",
    FEE = "fee",
    SUBTOTAL = "sub_total",
    GIFT = "gift",
    GRATUITY = "gratuity",
    PACKAGE_DISCOUNT = "package_discount",
    PICKUP = "pickup",
    LEGACY_PLUGIN_FEE = "legacy_plugin_fee",
    VOID_DISCOUNT = "void_discount",
    COUPON_REDEEM = "coupon_redeem",
    PROMOTIONAL_DISCOUNT = "promotional_discount",
    PROMOTIONAL_DISCOUNT_REDEEM = "promotional_discount_redeem",
    TYPE_ADHOC_ADJUSTMENT = "adhoc_adjustment",
    FEEABLE_TAXABLE_ADJUSTMENT = "feeable_taxable_adjustment",
    PARTNER_PACKAGE_ITEM = "partner_package_item",
    PARTNER_PACKAGE_DISCOUNT = "partner_package_discount",
    PARTNER_PURCHASE_ITEM = "partner_purchase_item",
    PARTNER_PURCHASE_SUMMARY_ITEM = "partner_purchase_summary_item",
    GRATUITY_TRANSFER = "gratuity_transfer",
}

export enum LineItemStatus {
    PENDING = "pending",
    COMPLETE = "complete",
    FAILED = "failed",
}

export enum LineItemObject {
    DEFAULT = "line_item",
    PAYMENT = "payment_line_item",
    REFUND = "refund_line_item",
    COUPON = "coupon_line_item",
    COUPON_REDEEM = "coupon_redeem_line_item",
    DISCOUNT_CODE = "discount_code_line_item",
    CODE_REDEEM = "code_redeem_line_item",
}

export enum LineItemVisibility {
    NONE = "none", // Don't show to anyone
    ALL = "all", // show it to everyone
    SELLER = "seller", // show it to seller only
    TRAVELER = "traveler", // show to traveler only, like flex standard
}

export enum LineItemSource {
    SELLER = "seller",
    TRAVELER = "traveler",
}

export enum LineItemDestination {
    SELLER = "seller",
    TRAVELER = "traveler",
}

export interface LineItemTemplate {
    id: ID;
    object: LineItemTemplateObject;
    type: LineItemType;
    code: LineItemCode;
    name: string;
    destination: LineItemDestination;
    visible: boolean;
    visibility: LineItemVisibility;
    system: boolean;
    includeInAmount: boolean;
    seller: { id: ID };
    createdBy: { id: ID };
    createdAt: Dayjs;
    updatedAt: Dayjs;
    unit?: Unit;
}

export enum LineItemTemplateCode {
    PER_OUTING_PRICE = "per_outing_price",
    PRIVATE_UPGRADE = "private_upgrade",
}

export enum LineItemTemplateObject {
    DEFAULT = "line_item_template",
}

export type LineItemSelectorType = LineItemType.DEMOGRAPHIC | LineItemType.EQUIPMENT | LineItemType.ADDON;

export enum LineItemDisputeStatus {
    WON = "won",
    LOST = "lost",
    PENDING = "pending",
}

export enum RedemptionAction {
    REDEEM = "redeem",
    REMOVE = "remove",
}
